'use client';
import { Markdown } from '@repo/ui/src/components/Markdown';
import { classNames } from '@repo/ui/src/utils/classNames';
import { headingClasses, displayClasses, textAlignClasses } from '@repo/ui/src/utils/styleUtils';
import { ToutContentButtons } from './_toutContentButtons';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import type { ImageProps, MediaType } from '@repo/ui/src/types';
import { appConfig } from '../../utils/appConfig';
import React from 'react';

const lightHeadingClasses = {
  h1: displayClasses.h1Light,
  h2: displayClasses.h2Light,
  h3: headingClasses.h3,
  h4: headingClasses.h4,
  h5: headingClasses.h5,
  h6: headingClasses.h6,
  h7: headingClasses.h7,
  p: '',
};

export const ToutContent = ({
  align = 'center',
  heading,
  headingLight,
  headerVariation,
  lilHeaderVariation,
  headingStyleVariant,
  lilHeading,
  highlight,
  description,
  flexible,
  fullWidth,
  buttons = [],
  buttonSize,
  buttonNegative = false,
  dark,
  divider,
  forceDivider,
  lineClamp = 0,
  grillRecommendation,
  narrowContent,
  youtubeLink,
  narrowDividerMargin,
  fullWidthDivider,
  icon,
  className,
  style = {},
}: ToutContentProps) => {
  const hasButtons = buttons?.length || youtubeLink;
  const HeaderEl = headerVariation?.headerVariation || 'h2';
  const LilHeaderEl = lilHeaderVariation?.headerVariation || 'p';
  const iconImage = (icon as ImageProps)?.image;
  const iconMedia = icon as MediaType;
  const hasIconData = !!(iconImage || iconMedia?.url || iconMedia?.name);

  return (
    <div className={className || undefined} style={style}>
      <div className={classNames(dark && 'text-neutral-100 [&_p]:text-neutral-100')}>
        {icon && hasIconData && (
          <LazyLoadImage
            imgComponent={
              <img
                height="32px"
                src={buildImageSrc(icon, 32, 1, 1)}
                alt={(icon as ImageProps).image?.name ?? (icon as MediaType).name}
              />
            }
            className={classNames('icon mb-4', textAlignClasses[align])}
          />
        )}

        {lilHeading && (
          <LilHeaderEl
            className={classNames(
              'tra-text-web-sub-4 mb-2 w-full',
              dark && 'text-neutral-100',
              flexible && 'flexible',
              narrowContent && 'w-[288px] max-w-full',
              textAlignClasses[align],
            )}
          >
            {lilHeading}
          </LilHeaderEl>
        )}

        {(heading || headingLight) && (
          <HeaderEl
            className={classNames(
              'heading',
              dark && 'text-neutral-100',
              flexible && 'flexible',
              textAlignClasses[align],
              headingStyleVariant === 'h1'
                ? displayClasses[headingStyleVariant]
                : headingClasses[headingStyleVariant ?? HeaderEl],
              !!(highlight || description || forceDivider || hasButtons) &&
                !narrowDividerMargin &&
                'mb-6',
              (forceDivider || (divider && description)) && narrowDividerMargin ? 'mt-4' : 'mt-6',
            )}
          >
            {headingLight && (
              <span
                className={classNames(
                  appConfig.isTraeger &&
                    headingStyleVariant &&
                    lightHeadingClasses[headingStyleVariant],
                  appConfig.isTraeger && 'font-display46 font-normal text-inherit',
                  align === 'center' ? 'flex justify-center' : 'block',
                )}
              >
                {headingLight}
              </span>
            )}
            <span className="hidden">&nbsp;</span>
            {heading}
          </HeaderEl>
        )}

        {(forceDivider || (divider && description)) && (
          <div
            className={classNames(
              'flex',
              align === 'left' && 'justify-start',
              align === 'right' && 'justify-end',
              align === 'center' && 'justify-center',
            )}
          >
            <hr
              className={classNames(
                'border-none mb-6 bg-brand-300',
                appConfig.isMeater ? 'h-0.5' : 'h-1',
                fullWidthDivider ? 'w-full' : 'w-12',
              )}
            />
          </div>
        )}
        <div className="bottom-content">
          {highlight && (
            <p
              className={classNames(
                'font-bold',
                dark && 'text-neutral-100 grid',
                flexible && 'flexible',
                narrowContent &&
                  'w-[320px] md:w-auto md:max-w-[350px] motion-reduce:w-auto motion-reduce:max-w-[350px]',
                lineClamp > 0 && 'line-clamp-4',
                fullWidth && 'max-w-[776px]',
                !!description && 'mb-6',
                textAlignClasses[align],
              )}
            >
              {highlight}
            </p>
          )}

          {description && (
            <Markdown
              components={{
                ol: ({ children }: { children: React.ReactNode }) => (
                  <ol className={classNames('list-none m-0 pt-2')}>{children}</ol>
                ),
                li: ({ children }: { children: React.ReactNode }) => (
                  <li className={classNames('mb-4 relative')}>{children}</li>
                ),
                ul: ({ children }: { children: React.ReactNode }) => (
                  <ul
                    className={classNames(
                      align === 'left' && 'text-left',
                      align === 'right' && 'text-right',
                      align === 'center' && 'text-center',
                    )}
                  >
                    {children}
                  </ul>
                ),
              }}
              className={classNames(
                'description body',
                textAlignClasses[align],
                dark && 'text-neutral-100',
                narrowContent &&
                  'w-[320px] md:w-auto md:max-w-[350px] motion-reduce:w-auto motion-reduce:max-w-[350px]',
                lineClamp > 0 && 'overflow-hidden',
                fullWidth && 'lg:max-w-[776px]',
                hasButtons && 'mb-6',
                align === 'left' ? 'ml-0' : 'ml-auto',
                align === 'right' ? 'mr-0' : 'mr-auto',
                'max-w-full lg:max-w-[45ch]',
              )}
              bodySize={flexible ? 'xLarge' : 'large'}
            >
              {description}
            </Markdown>
          )}

          <ToutContentButtons
            buttons={buttons}
            dark={dark}
            align={align}
            buttonSize={buttonSize}
            grillRecommendation={grillRecommendation}
            youtubeLink={youtubeLink}
            buttonNegative={buttonNegative}
          />
        </div>
      </div>
    </div>
  );
};
